import React, { useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'react-datepicker/dist/react-datepicker.css';
import { NavLink, useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// API URLs
const API_URL = `${BQ_BASE_URL}/bq/api/rooms/search`;
const AVAILABILITY_API_URL = `${BQ_BASE_URL}/bq/api/availability`;

const RoomAssignment = () => {
  const navigate = useNavigate();
  const [roomData, setRoomData] = useState({});
  const [availableRoomTypes, setAvailableRoomTypes] = useState([]);
  const [isPopOpen, setIsPopOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [updatedRoom, setUpdatedRoom] = useState(selectedRoom);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [roomStatus, setRoomStatus] = useState('all');
  const [selectedRoomType, setSelectedRoomType] = useState('all');
  const [onlyAvailableRooms, setOnlyAvailableRooms] = useState(false);

  useEffect(() => {
    setUpdatedRoom(selectedRoom);
  }, [selectedRoom]);

  const handleUpdate = async () => {
    // Show error toast that room status cannot be changed here
    toast.error(
      <div>
        <p className="font-bold">Room status cannot be changed here!</p>
        <p className="text-sm mt-1">
          Please go to{' '}
          <button
            onClick={() => {
              toast.dismiss();
              navigate(
                '/dashboard/core-modules/property-management-system/master-data/room-management'
              );
            }}
            className="text-blue-600 underline font-semibold hover:text-blue-800"
          >
            Master Data → Room Management
          </button>{' '}
          to change room status.
        </p>
      </div>,
      {
        position: 'top-center',
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: false,
        draggable: false,
        className: 'bg-red-50 border-l-4 border-red-500',
      }
    );

    // Close the modal after showing error
    setTimeout(() => {
      modalClose();
    }, 1000);
  };

  const handleViewOnly = () => {
    // Navigate to the correct page where room status can be changed
    navigate('/dashboard/core-modules/property-management-system/master-data/room-management');
  };

  // Fetch room details and available room types
  useEffect(() => {
    const fetchRoomDetails = async () => {
      try {
        const response = await fetch(API_URL);
        const data = await response.json();

        if (data && data.rooms && typeof data.rooms === 'object') {
          const formattedRoomData = {};

          Object.keys(data.rooms).forEach((category) => {
            formattedRoomData[category] = {};
            Object.keys(data.rooms[category]).forEach((status) => {
              let rooms = data.rooms[category][status];

              // Check if rooms is an object that contains a 'rooms' array
              if (rooms && rooms.rooms && Array.isArray(rooms.rooms)) {
                formattedRoomData[category][status] = rooms.rooms;
              } else if (Array.isArray(rooms)) {
                // If rooms is already an array, use it directly
                formattedRoomData[category][status] = rooms;
              } else if (typeof rooms === 'object') {
                console.warn(
                  `Expected array for category "${category}" and status "${status}", but got:`,
                  rooms
                );

                // Convert the object to an array (e.g., use Object.values to get array of room objects)
                formattedRoomData[category][status] = Object.values(rooms);
              } else {
                console.warn(
                  `Unexpected type for rooms in category "${category}" and status "${status}"`
                );
              }
            });
          });

          setRoomData(formattedRoomData);
        } else {
          console.warn('Expected rooms data in an object format, but got:', data);
        }
      } catch (error) {
        console.error('Error fetching room details:', error);
      }
    };

    const fetchAvailableRoomTypes = async () => {
      try {
        const response = await fetch(AVAILABILITY_API_URL);
        const data = await response.json();
        setAvailableRoomTypes(data.available_room_types);
      } catch (error) {
        console.error('Error fetching available room types:', error);
        setAvailableRoomTypes([]);
      }
    };

    fetchRoomDetails();
    fetchAvailableRoomTypes();
  }, []);

  // Filter rooms based on search criteria
  const filteredRooms = Object.keys(roomData).map((roomType) => {
    const rooms = Object.keys(roomData[roomType]).flatMap((status) =>
      roomData[roomType][status].filter((room) => {
        const matchesSearch =
          room.baseprice?.toString().includes(searchTerm) ||
          room.roomnumber?.toString().includes(searchTerm);
        const matchesStatus = roomStatus === 'all' || room.status?.toLowerCase() === roomStatus;
        const matchesRoomType =
          selectedRoomType === 'all' ||
          room.room_type?.toLowerCase() === selectedRoomType.toLowerCase();

        const matchesAvailability =
          !onlyAvailableRooms || room.status?.toLowerCase() === 'available';

        return matchesSearch && matchesStatus && matchesRoomType && matchesAvailability;
      })
    );

    // Count the rooms based on their status
    const availableCount = rooms.filter(
      (room) => room.status?.toLowerCase() === 'available'
    ).length;
    const occupiedCount = rooms.filter((room) => room.status?.toLowerCase() === 'occupied').length;
    const reservedCount = rooms.filter((room) => room.status?.toLowerCase() === 'reserved').length;
    const maintenanceCount = rooms.filter(
      (room) => room.status?.toLowerCase() === 'under maintenance'
    ).length;

    // Return the filtered roomType and count of rooms for each status
    return {
      roomType,
      availableCount,
      occupiedCount,
      reservedCount,
      maintenanceCount,
      rooms,
    };
  });

  const handleClearFilters = () => {
    setSearchTerm('');
    setRoomStatus('all');
    setSelectedRoomType('all');
    setOnlyAvailableRooms(false);
  };

  const openModal = (room) => {
    setSelectedRoom(room);
    setIsPopOpen(true);
  };

  const modalClose = () => {
    setIsPopOpen(false);
    setSelectedRoom(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSelectedRoom((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <NavLink
        to="/dashboard/core-modules/property-management-system/front-desk-operations"
        className="flex items-center text-gray-400 hover:text-black mb-4"
      >
        <FaArrowLeft className="mr-3 text-md" />
        <span className="text-lg">Back to Front Desk Operations</span>
      </NavLink>

      <h1 className="text-2xl font-bold text-center text-black">Room Management</h1>
      <h1 className="text-sm font-poppins font-semibold text-center mb-1 text-gray-600">
        Efficient room management ensures seamless reservations, maintenance and comfort, providing
        guests with an exceptional experience throughout their stay.
      </h1>
      <hr className="border border-gray-400 w-1/2 mx-auto mb-7" />

      {/* Filters Section */}
      <div className="flex justify-between items-center gap-2 mb-8 text-sm w-full">
        <div className="w-full mb-4 sm:mb-0 relative">
          <input
            type="text"
            className="w-full px-2 py-2 pl-10 border border-gray-300 rounded-md"
            placeholder="Search by Room Number"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FiSearch
            size={16}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600"
          />
        </div>

        <div className="w-full mb-4 sm:mb-0">
          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            value={roomStatus}
            onChange={(e) => setRoomStatus(e.target.value)}
          >
            <option value="all" disabled>
              Rooms Status
            </option>
            <option value="available">Available</option>
            <option value="reserved">Reserved</option>
            <option value="occupied">Occupied</option>
            <option value="under maintenance">Under Maintenance</option>
          </select>
        </div>
        <div className="w-full mb-4 sm:mb-0">
          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none"
            value={selectedRoomType}
            onChange={(e) => {
              setSelectedRoomType(e.target.value);
            }}
          >
            <option value="all" disabled>
              All Room Types
            </option>
            {roomData &&
              Object.keys(roomData).map((roomType) => (
                <option key={roomType} value={roomType}>
                  {roomType}
                </option>
              ))}
          </select>
        </div>

        <div className="w-full mr-4">
          <button
            onClick={handleClearFilters}
            className="w-full px-10 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Status Cards */}

      {/* Room Table */}
      <div className="space-y-8 text-sm">
        {filteredRooms.map(({ roomType, rooms }) => {
          if (rooms.length === 0) return null;

          return (
            <div key={roomType}>
              {/* Room Type Header */}
              <h2 className="bg-white text-black font-bold text-left p-3 text-base rounded-t-lg shadow-md">
                {roomType}
              </h2>

              {/* Table Container */}
              <div className="overflow-x-auto rounded-b-lg">
                <table className="min-w-full table-auto bg-white">
                  {/* Table Header */}
                  <thead className="bg-gray-300 text-black">
                    <tr>
                      <th className="py-3 px-6 text-left font-medium w-1/6">Room No.</th>
                      <th className="py-3 px-6 text-left font-medium w-1/6">Room Type</th>
                      <th className="py-3 px-6 text-left font-medium w-1/6">Status</th>
                      <th className="py-3 px-6 text-left font-medium w-1/6">Price</th>
                    </tr>
                  </thead>
                  {/* Table Body */}
                  <tbody className="text-gray-700 border border-gray-200">
                    {rooms.map((room, index) => (
                      <tr
                        key={room.id || index}
                        className={`${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-100'
                        } hover:bg-gray-200 cursor-pointer`}
                        onClick={() => openModal(room)}
                      >
                        <td className="py-3 px-6">{room.roomnumber}</td>
                        <td className="py-3 px-6">{room.room_type}</td>
                        <td className="py-3 px-6">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              room.status?.toLowerCase() === 'available'
                                ? 'bg-green-100 text-green-800'
                                : room.status?.toLowerCase() === 'occupied'
                                  ? 'bg-red-100 text-red-800'
                                  : room.status?.toLowerCase() === 'reserved'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {room.status}
                          </span>
                        </td>
                        <td className="py-3 px-6">₹{room.baseprice}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal for Viewing Room Details */}
      {isPopOpen && selectedRoom && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg w-1/3 text-sm text-black">
            <h3 className="text-xl font-bold text-center">Room Details</h3>
            <p className="text-sm text-red-500 mb-4 text-center font-semibold bg-red-50 p-2 rounded-lg">
              ⚠️ Room status cannot be changed here. Please use Master Data → Room Management.
            </p>
            <div className="space-y-4 text-xs">
              <div>
                <label className="block text-black font-medium">Room Number</label>
                <input
                  type="text"
                  name="roomnumber"
                  value={selectedRoom.roomnumber}
                  className="w-full px-4 py-2 border rounded-md bg-gray-100 cursor-not-allowed"
                  disabled={true}
                />
              </div>
              <div>
                <label className="block text-black font-medium">Room Type</label>
                <input
                  type="text"
                  name="room_type"
                  value={selectedRoom.room_type}
                  className="w-full px-4 py-2 border rounded-md bg-gray-100 cursor-not-allowed"
                  disabled={true}
                />
              </div>
              <div>
                <label className="block text-black font-medium">Status</label>
                <select
                  name="status"
                  value={selectedRoom.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-md bg-yellow-50 border-yellow-300"
                >
                  <option value="Available">Available</option>
                  <option value="Reserved">Reserved</option>
                  <option value="Occupied">Occupied</option>
                  <option value="Under Maintenance">Under Maintenance</option>
                </select>
                <p className="text-xs text-red-500 mt-1">
                  * Status changes are disabled in this view
                </p>
              </div>
              <div>
                <label className="block text-black font-medium">Price</label>
                <input
                  type="number"
                  name="baseprice"
                  value={selectedRoom.baseprice}
                  className="w-full px-4 py-2 border rounded-md bg-gray-100 cursor-not-allowed"
                  disabled={true}
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={modalClose}
                className="bg-red-500 text-white hover:bg-red-600 font-semibold px-6 py-2 rounded-md transition-colors"
              >
                Close
              </button>
              {/* <button
                onClick={handleViewOnly}
                className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-md transition-colors"
              >
                Go to Room Management
              </button> */}
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover
        className="mt-12"
      />
    </div>
  );
};

export default RoomAssignment;
